import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@components/common/Modal/Modal';
import Input from '@components/Form/Input/Input';
import Button from '@components/Button/Button';
import { applyServerValidationErrors } from '@/utils/apiValidationErrors';

export interface MentorForm {
    name: string;
    email: string;
    speciality: string;
}

export interface CreateMentorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit?: (data: MentorForm) => void | Promise<void>;
    isSubmitting?: boolean;
}

const CreateMentorModal = ({
    isOpen,
    onClose,
    onSubmit,
    isSubmitting = false,
}: CreateMentorModalProps) => {
    const { t } = useTranslation('mentors');
    const {
        register,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<MentorForm>({
        defaultValues: {
            name: '',
            email: '',
            speciality: '',
        },
    });

    useEffect(() => {
        if (!isOpen) {
            reset({
                name: '',
                email: '',
                speciality: '',
            });
            return;
        }
    }, [isOpen, reset]);

    const handleClose = () => {
        clearErrors();
        reset({
            name: '',
            email: '',
            speciality: '',
        });
        onClose();
    };

    const handleFormSubmit = async (data: MentorForm) => {
        clearErrors();
        try {
            await Promise.resolve(onSubmit?.(data));
            reset({
                name: '',
                email: '',
                speciality: '',
            });
        } catch (error) {
            applyServerValidationErrors<MentorForm>(error, setError);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title={t('modal.create_title')}>
            <form
                id="create-mentor-form"
                onSubmit={handleSubmit(handleFormSubmit)}
                noValidate
                className="overflow-y-auto p-4 2xl:p-5 flex flex-col gap-4"
            >
                <Input
                    label={t('modal.name_label')}
                    placeholder={t('modal.name_placeholder')}
                    required
                    registration={register('name', {
                        required: t('modal.validation.name_required'),
                        validate: value =>
                            value.trim().length > 0 ||
                            t('modal.validation.name_empty'),
                    })}
                    error={errors.name?.message}
                />

                <Input
                    label={t('modal.email_label')}
                    placeholder={t('modal.email_placeholder')}
                    required
                    type="email"
                    registration={register('email', {
                        required: t('modal.validation.email_required'),
                        pattern: {
                            value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                            message: t('modal.validation.email_invalid'),
                        },
                    })}
                    error={errors.email?.message}
                />

                <Input
                    label={t('modal.speciality_label')}
                    placeholder={t('modal.speciality_placeholder')}
                    required
                    registration={register('speciality', {
                        required: t('modal.validation.speciality_required'),
                        validate: value =>
                            value.trim().length > 0 ||
                            t('modal.validation.speciality_empty'),
                    })}
                    error={errors.speciality?.message}
                />
            </form>

            <div className="flex items-center justify-between border-t border-[var(--neutral-color)] px-4 2xl:px-5 py-2 2xl:py-3">
                <Button type="button" variant="dark" onClick={handleClose} disabled={isSubmitting}>
                    {t('modal.cancel_button')}
                </Button>

                <Button type="submit" variant="primary" form="create-mentor-form" disabled={isSubmitting}>
                    {isSubmitting ? t('modal.creating_button') : t('modal.create_button')}
                </Button>
            </div>
        </Modal>
    );
};

export default CreateMentorModal;
